import { useState } from "react";
import { Link } from "react-router-dom";
import { Pagination } from "@mui/material";
import { EmptyState, ErrorPanel, LoadingPanel, PageHeader, Panel } from "../../components/ui";
import { partnerQueryKeys } from "../../api/queryKeys";
import { fetchAllPartnerOffers } from "../../api/endpoints/program";
import usePartnerQuery from "../../hooks/usePartnerQuery";
import OfferCard from "../Dashboard/OfferCard";

const PAGE_SIZE = 10;

const OfferList = ({ title, offers }) =>
  offers.length ? (
    <Panel title={title}>
      <ul className="list-none p-0 m-0 grid grid-cols-1 gap-3 md:grid-cols-2" aria-label={title}>
        {offers.map((offer) => (
          <OfferCard key={offer.id} offer={offer} />
        ))}
      </ul>
    </Panel>
  ) : null;

/**
 * Every offer the office has published: running ones first, then the ones that have ended. The
 * server orders them that way across pages, so a page is split where the running ones stop.
 */
const AllOffersPage = () => {
  const [page, setPage] = useState(0);
  const { data, isLoading, isError, refetch } = usePartnerQuery(
    partnerQueryKeys.allOffers(page),
    () => fetchAllPartnerOffers({ page, size: PAGE_SIZE }),
    { keepPreviousData: true }
  );

  const offers = data?.content || [];
  const running = offers.filter((offer) => !offer.ended);
  const ended = offers.filter((offer) => offer.ended);
  const totalPages = data?.totalPages || 0;

  return (
    <div>
      <Link
        to="/partner/dashboard"
        className="inline-block mb-4 text-xs font-semibold text-teal-700 no-underline hover:text-teal-800"
      >
        ← Back to dashboard
      </Link>
      <PageHeader title="All offers" />

      {isLoading ? (
        <LoadingPanel label="Loading offers…" />
      ) : isError ? (
        <ErrorPanel
          description="The server did not answer. Nothing is lost, try again."
          action={
            <button
              type="button"
              onClick={() => refetch()}
              className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white"
            >
              Try again
            </button>
          }
        />
      ) : offers.length === 0 ? (
        <EmptyState
          title="No offers yet"
          description="Offers the office runs for partners will show up here."
        />
      ) : (
        <div className="space-y-4">
          <OfferList title="Running now" offers={running} />
          <OfferList title="Ended" offers={ended} />
          {totalPages > 1 ? (
            <div className="flex justify-center">
              <Pagination
                count={totalPages}
                page={page + 1}
                onChange={(_, next) => setPage(next - 1)}
                size="small"
              />
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default AllOffersPage;
